import { useEffect, useMemo, useState } from 'react';
import { events as fallbackEvents } from '../data/events';
import { EVENTS_TABLE, isSupabaseConfigured, supabase } from '../supabaseClient';

const VENUE_TZ = 'America/Chicago';

function parseYmdToUtcNoon(dateStr) {
  // Supabase "date" comes as YYYY-MM-DD (date-only). Parsing via new Date(dateStr)
  // treats it as UTC midnight and can shift to the previous day in US timezones.
  // Using UTC noon keeps the calendar date stable in all timezones.
  if (!dateStr || typeof dateStr !== 'string') return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr.trim());
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  if (!Number.isFinite(y) || !Number.isFinite(mo) || !Number.isFinite(d)) return null;
  return new Date(Date.UTC(y, mo - 1, d, 12, 0, 0));
}

function formatDateShort(dateStr) {
  if (!dateStr) return '';
  const d = parseYmdToUtcNoon(dateStr);
  if (!d) return '';
  return new Intl.DateTimeFormat('en-US', {
    timeZone: VENUE_TZ,
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  }).format(d);
}

function normalizeEvent(row) {
  const isActive = row.is_active !== false;
  return {
    id: row.id,
    date: row.date,
    formattedDate: formatDateShort(row.date) || row.formattedDate || '',
    title: row.title,
    subtitle: row.subtitle || '',
    djName: row.dj_name || row.djName || row.title || '',
    description: row.description || '',
    bookingUrl: row.booking_url || row.bookingUrl || '',
    imageUrl: row.image_url || row.imageUrl || row.image || '',
    isActive
  };
}

function normalizeDateValue(v) {
  if (!v) return 0;
  const d = parseYmdToUtcNoon(v);
  const t = d ? d.getTime() : 0;
  return Number.isFinite(t) ? t : 0;
}

function sortEvents(a, b) {
  return normalizeDateValue(a.date) - normalizeDateValue(b.date);
}

export function useSupabaseEvents() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);

      if (!isSupabaseConfigured || !supabase) {
        setRows(fallbackEvents);
        setLoading(false);
        return;
      }

      // Fetch without server-side ordering so we don't 400 if columns differ/missing.
      const { data, error } = await supabase.from(EVENTS_TABLE).select('*');

      if (cancelled) return;

      if (error) {
        setError(error);
        setRows(fallbackEvents);
        setLoading(false);
        return;
      }

      const safe = Array.isArray(data) ? data : [];
      setRows((safe.length > 0 ? safe : fallbackEvents).slice().sort(sortEvents));
      setLoading(false);
    }

    load();

    return () => {
      cancelled = true;
    };
  }, []);

  const events = useMemo(() => rows.map(normalizeEvent), [rows]);

  return { events, loading, error, reload: async () => {} };
}
